import type { Level } from '@color-dots/game';

export type VisualStyle =
  | 'pipe'
  | 'snake'
  | 'gear'
  | 'chain'
  | 'rope'
  | 'wave'
  | 'crystal'
  | 'lightning'
  | 'spring'
  | 'thread'
  | 'bubble'
  | 'ribbon'
  | 'fire'
  | 'vine'
  | 'magnet'
  | 'candy'
  | 'bone'
  | 'magic'
  | 'cyberpunk'
  | 'goth'
  | 'robot'
  | 'wheel'
  | 'emo'
  | 'armor'
  | 'chrome'
  | 'viking'
  | 'samurai'
  | 'zombie'
  | 'toxic'
  | 'demon';

export const VISUAL_STYLE_ORDER: VisualStyle[] = [
  'pipe',
  'snake',
  'gear',
  'chain',
  'rope',
  'wave',
  'crystal',
  'lightning',
  'spring',
  'thread',
  'bubble',
  'ribbon',
  'fire',
  'vine',
  'magnet',
  'candy',
  'bone',
  'magic',
  'cyberpunk',
  'goth',
  'robot',
  'wheel',
  'emo',
  'armor',
  'chrome',
  'viking',
  'samurai',
  'zombie',
  'toxic',
  'demon',
];

const STYLE_LABELS: Record<VisualStyle, string> = {
  pipe: 'Трубы',
  snake: 'Змеи',
  gear: 'Шестерни',
  chain: 'Цепи',
  rope: 'Канаты',
  wave: 'Волны',
  crystal: 'Кристаллы',
  lightning: 'Молнии',
  spring: 'Пружины',
  thread: 'Нити',
  bubble: 'Пузыри',
  ribbon: 'Ленты',
  fire: 'Огонь',
  vine: 'Лозы',
  magnet: 'Магниты',
  candy: 'Конфеты',
  bone: 'Кости',
  magic: 'Магия',
  cyberpunk: 'Киберпанк',
  goth: 'Готика',
  robot: 'Роботы',
  wheel: 'Колёса',
  emo: 'Эмо',
  armor: 'Броня',
  chrome: 'Хром',
  viking: 'Викинги',
  samurai: 'Самураи',
  zombie: 'Зомби',
  toxic: 'Токсик',
  demon: 'Демоны',
};

export const STYLE_ICONS: Record<VisualStyle, string> = {
  pipe: '🔩',
  snake: '🐍',
  gear: '⚙️',
  chain: '⛓️',
  rope: '🪢',
  wave: '🌊',
  crystal: '💎',
  lightning: '⚡',
  spring: '🌀',
  thread: '🧵',
  bubble: '🫧',
  ribbon: '🎀',
  fire: '🔥',
  vine: '🌿',
  magnet: '🧲',
  candy: '🍬',
  bone: '🦴',
  magic: '🪄',
  cyberpunk: '🤖',
  goth: '🦇',
  robot: '🦾',
  wheel: '🛞',
  emo: '🖤',
  armor: '🛡️',
  chrome: '🏍️',
  viking: '🪓',
  samurai: '🗡️',
  zombie: '🧟',
  toxic: '☣️',
  demon: '👹',
};

export function getLevelNumber(levelId: string): number {
  const match = levelId.match(/\d+/);
  return match ? parseInt(match[0], 10) : 1;
}

// Sums to 150 — the current total level count (120 original + 30 in expert-01).
// Now an even 5 per style across all 30 styles (30×5 = 150) — the cleanest
// possible split, adopted 2026-09-19 when 10 more "tough" styles (robot,
// wheel, emo, armor, chrome, viking, samurai, zombie, toxic, demon) joined
// cyberpunk/goth per the user's request, rather than reshuffling uneven
// per-style weights again. Nothing here needs to stay in sync with level
// count by construction (getVisualStyle wraps via modulo), but keeping the
// sum matched to the real total means every style gets a turn within one
// full pass instead of the cycle repeating with a gap or overlap.
const STYLE_RANGE_COUNTS: [VisualStyle, number][] = [
  ['pipe', 5],
  ['snake', 5],
  ['gear', 5],
  ['chain', 5],
  ['rope', 5],
  ['wave', 5],
  ['crystal', 5],
  ['lightning', 5],
  ['spring', 5],
  ['thread', 5],
  ['bubble', 5],
  ['ribbon', 5],
  ['fire', 5],
  ['vine', 5],
  ['magnet', 5],
  ['candy', 5],
  ['bone', 5],
  ['magic', 5],
  ['cyberpunk', 5],
  ['goth', 5],
  ['robot', 5],
  ['wheel', 5],
  ['emo', 5],
  ['armor', 5],
  ['chrome', 5],
  ['viking', 5],
  ['samurai', 5],
  ['zombie', 5],
  ['toxic', 5],
  ['demon', 5],
];

/**
 * Smooth weighted round-robin (the algorithm nginx uses for weighted load
 * balancing): spreads each style evenly across all slots in proportion to its
 * quota, instead of one contiguous block per style. Quotas are unchanged
 * (pipe still gets exactly 8 of 150, snake 8, etc.) — only which level number
 * gets which style changes, so a player sees every style within the first
 * ~18 levels instead of waiting a long block for the last one.
 */
const STYLE_BY_POSITION: VisualStyle[] = (() => {
  const total = STYLE_RANGE_COUNTS.reduce((sum, [, count]) => sum + count, 0);
  const currentWeights = STYLE_RANGE_COUNTS.map(([, count]) => count);
  const result: VisualStyle[] = [];

  for (let i = 0; i < total; i++) {
    let selectedIndex = 0;
    for (let j = 1; j < currentWeights.length; j++) {
      if (currentWeights[j]! > currentWeights[selectedIndex]!) selectedIndex = j;
    }

    result.push(STYLE_RANGE_COUNTS[selectedIndex]![0]);
    currentWeights[selectedIndex] -= total;
    for (let j = 0; j < currentWeights.length; j++) {
      currentWeights[j] += STYLE_RANGE_COUNTS[j]![1];
    }
  }

  return result;
})();

export function getVisualStyle(levelNumber: number): VisualStyle {
  const index = (levelNumber - 1) % STYLE_BY_POSITION.length;
  return STYLE_BY_POSITION[index]!;
}

export function getVisualStyleLabel(style: VisualStyle): string {
  return STYLE_LABELS[style];
}

export function getVisualStyleForLevel(levelId: string): VisualStyle {
  return getVisualStyle(getLevelNumber(levelId));
}

export interface VisualStyleGroup {
  style: VisualStyle;
  label: string;
  icon: string;
  levels: Level[];
}

export function groupLevelsByVisualStyle(levels: Level[]): VisualStyleGroup[] {
  const groups = new Map<VisualStyle, Level[]>();

  for (const level of levels) {
    const style = getVisualStyleForLevel(level.id);
    const bucket = groups.get(style) ?? [];
    bucket.push(level);
    groups.set(style, bucket);
  }

  return VISUAL_STYLE_ORDER.filter((style) => groups.has(style)).map((style) => ({
    style,
    label: getVisualStyleLabel(style),
    icon: STYLE_ICONS[style],
    levels: groups.get(style)!,
  }));
}

export function getStylesPresentInLevels(levels: Level[]): VisualStyle[] {
  const present = new Set<VisualStyle>();
  for (const level of levels) {
    present.add(getVisualStyleForLevel(level.id));
  }
  return VISUAL_STYLE_ORDER.filter((style) => present.has(style));
}
